import { useState } from 'react';
import { Link } from 'react-router-dom';
import { rule } from '../../lib/fieldRules.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Seo } from '../../components/Seo.jsx';
import { Stepper } from '../../components/Stepper.jsx';
import { SignInButtons } from '../../components/SignInButtons.jsx';
import { SocialConnect, useSocialAccounts } from '../../components/SocialConnect.jsx';
import { Button, Field, Input, Alert, Card, Spinner } from '../../components/ui.jsx';

const STEPS = [
  { key: 'account', label: 'Account' },
  { key: 'socials', label: 'Connect your socials' },
];

/**
 * Getting started as a creator, for people who arrive without a program in mind.
 *
 * Two short steps: the account (skipped when you are already signed in), then
 * your social accounts, whose followers go on your profile as verified. From
 * there, build the profile or browse programs first, either way round — but
 * applying needs one connected account. "Finish later" is always available.
 */
export default function CreatorStart() {
  const { user, loading, isCreator, signup, addRole } = useAuth();
  const accountDone = Boolean(user) && isCreator;
  const social = useSocialAccounts({ skip: !accountDone });
  const [account, setAccount] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // 'account' is only ever shown while there is still an account to make.
  const [forced, setForced] = useState(null);

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }

  const step = forced ?? (accountDone ? 'socials' : 'account');
  const connected = (social.data?.accounts ?? []).length > 0;
  const setAcct = (key) => (e) => setAccount((a) => ({ ...a, [key]: e.target.value }));

  async function createAccount(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      if (!user) await signup({ ...account, role: 'creator' });
      else await addRole('creator');
      setForced('socials');
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title="Get started as a creator" noindex />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <Stepper
          steps={STEPS}
          current={step}
          complete={[
            ...(accountDone || forced === 'socials' ? ['account'] : []),
            ...(connected ? ['socials'] : []),
          ]}
        />

        {step === 'account' ? (
          <>
            <h1 className="mt-6 text-3xl font-semibold tracking-tight">
              {user ? 'Add a creator profile' : 'Make your account'}
            </h1>
            <p className="mt-2 text-ink-muted">
              Free. Public programs show their full terms, so you can browse before you commit to
              anything.
            </p>

            {user ? (
              <div className="mt-6 space-y-4">
                <Alert>{error}</Alert>
                <p className="text-sm text-ink-muted">
                  You’re signed in as <span className="font-medium text-ink">{user.email}</span>.
                  Add a creator profile to apply to programs.
                </p>
                <Button onClick={createAccount} disabled={busy}>
                  {busy ? 'Adding…' : 'Add creator profile'}
                </Button>
              </div>
            ) : (
              <Card className="mt-6 space-y-4 p-4 sm:p-5">
                <SignInButtons role="creator" next="/start/creator" onSuccess={() => setError('')} onError={setError} />
                <form onSubmit={createAccount} className="space-y-4">
                  <Alert>{error}</Alert>
                  <Field label="Name" required error={fieldErrors.name} constraint={rule.max(120)}>
                    <Input
                      value={account.name}
                      onChange={setAcct('name')}
                      autoComplete="name"
                      required
                      maxLength={120}
                    />
                  </Field>
                  {/* "username", not "email": see Signup.jsx. */}
                  <Field label="Email" required error={fieldErrors.email} constraint={rule.email()}>
                    <Input
                      type="email"
                      name="email"
                      value={account.email}
                      onChange={setAcct('email')}
                      autoComplete="username"
                      required
                    />
                  </Field>
                  <Field
                    label="Password"
                    required
                    error={fieldErrors.password}
                    constraint={rule.password()}
                  >
                    <Input
                      type="password"
                      name="password"
                      value={account.password}
                      onChange={setAcct('password')}
                      autoComplete="new-password"
                      minLength={10}
                      required
                    />
                  </Field>
                  <Button type="submit" size="lg" disabled={busy}>
                    {busy ? 'Creating account…' : 'Create creator account'}
                  </Button>
                </form>
                <p className="text-sm text-ink-muted">
                  Already have an account?{' '}
                  <Link to="/login" className="font-medium text-accent hover:underline">
                    Sign in
                  </Link>
                </p>
              </Card>
            )}

            <div className="mt-6 flex flex-wrap gap-3 border-t border-rule pt-5">
              <Button to="/programs" variant="ghost">
                Browse programs first
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="mt-6 text-3xl font-semibold tracking-tight">Connect your socials</h1>
            <p className="mt-2 max-w-xl text-ink-muted">
              Your followers come straight from the platform and show as verified on your profile,
              so brands can trust them. Affily never posts. You need one connected account to
              apply to a program.
            </p>

            <div className="mt-6">
              <SocialConnect social={social} next="/start/creator" />
            </div>

            <div className="mt-6 flex flex-wrap gap-3 border-t border-rule pt-5">
              <Button to="/dashboard/creator/profile" size="lg">
                Build my profile
              </Button>
              <Button to="/programs" variant="outline" size="lg">
                Browse programs first
              </Button>
              <Button to="/dashboard/creator" variant="ghost" size="lg">
                Finish later
              </Button>
            </div>
          </>
        )}

        {step === 'account' && (
          <p className="mt-4 text-xs text-ink-faint">
            {user ? (
              <Link to="/dashboard" className="hover:text-ink">
                Finish later
              </Link>
            ) : (
              'You can leave at any point; nothing is saved until you create the account.'
            )}
          </p>
        )}
      </div>
    </>
  );
}
